// What shows over the page: an image of a message whole (the lightbox) — the others of the message by the
// arrows, to download or open in a tab of its own — and the dialogs: deleting a message, the user's name,
// picture and access code, and inviting to the meeting. Escape or a click beside them closes them.
import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { rename } from "../account.ts";
import { dropAvatar, remove, setAvatar } from "../chat.ts";
import { NotAnImage, pictureOf } from "../images.ts";
import { link } from "../meeting.ts";
import { MEETING_TITLE, changed, fileUrl, nameOf, show, state, type Message } from "../store.ts";
import { Access, LOCALHOST, QrCode } from "./Access.tsx";
import { Avatar } from "./Avatar.tsx";
import { Icon, IconButton } from "./Icon.tsx";
import { LegalLinks } from "./Legal.tsx";
import { RichLine } from "./Markdown.tsx";

export function Overlays() {
  return (
    <>
      {state.lightbox !== null && <Lightbox />}
      {state.dialog?.kind === "delete" && <DeleteDialog m={state.dialog.message} />}
      {state.dialog?.kind === "profile" && <ProfileDialog />}
      {state.dialog?.kind === "invite" && <InviteDialog />}
    </>
  );
}

/** Escape calls `close` while the caller shows. */
function useEscape(close: () => void, more?: (e: KeyboardEvent) => void): void {
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else more?.(e);
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  });
}

function closeLightbox(): void {
  state.lightbox = null;
  changed();
}

function Lightbox() {
  const lb = state.lightbox!;
  const f = lb.files[lb.index];
  const n = lb.files.length;
  const go = (k: number) => {
    state.lightbox = { files: lb.files, index: (lb.index + k + n) % n };
    changed();
  };
  useEscape(closeLightbox, (e) => {
    if (n > 1 && e.key === "ArrowLeft") go(-1);
    if (n > 1 && e.key === "ArrowRight") go(1);
  });
  const url = fileUrl(f.id);
  return (
    <div class="lightbox" onClick={closeLightbox} role="dialog" aria-label={f.name}>
      <img src={url} alt={f.name} onClick={(e) => e.stopPropagation()} />
      {n > 1 && (
        <>
          <button class="lightbox-go prev" title="Предыдущее" onClick={(e) => (e.stopPropagation(), go(-1))}>
            <Icon name="chevronLeft" size={28} />
          </button>
          <button class="lightbox-go next" title="Следующее" onClick={(e) => (e.stopPropagation(), go(1))}>
            <Icon name="chevronRight" size={28} />
          </button>
        </>
      )}
      <div class="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <span class="lightbox-name">{f.name}</span>
        <span class="muted">
          {f.width}×{f.height} · {bytes(f.size)}
          {n > 1 ? ` · ${lb.index + 1} из ${n}` : ""}
        </span>
        <a class="icon-button" href={url} download={f.name} title="Скачать" aria-label="Скачать">
          <Icon name="download" />
        </a>
        <a class="icon-button" href={url} target="_blank" rel="noopener" title="Открыть в новой вкладке" aria-label="Открыть в новой вкладке">
          <Icon name="external" />
        </a>
        <IconButton icon="close" title="Закрыть" onClick={closeLightbox} />
      </div>
    </div>
  );
}

/** `n` bytes, in the user's words. */
function bytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
}

function closeDialog(): void {
  state.dialog = null;
  changed();
}

/** A dialog: a title, what it holds, its buttons. */
function Dialog({ title, children }: { title: string; children: ComponentChildren }) {
  useEscape(closeDialog);
  return (
    <div class="dialog-back" onClick={closeDialog}>
      <div class="dialog" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function DeleteDialog({ m }: { m: Message }) {
  const yes = useRef<HTMLButtonElement>(null);
  useEffect(() => yes.current?.focus(), []);
  return (
    <Dialog title="Удалить сообщение">
      <p>Удалить это сообщение? Его не вернуть, а его изображения удалятся с сервера.</p>
      <div class="dialog-quote">
        <Avatar id={m.from} size={24} />
        <b>{nameOf(m.from)}</b>
        <span>{m.text.trim() !== "" ? <RichLine text={m.text} /> : <i>изображение</i>}</span>
      </div>
      <div class="dialog-buttons">
        <button onClick={closeDialog}>Отмена</button>
        <button ref={yes} class="danger" onClick={() => remove(m, true)}>
          Удалить
        </button>
      </div>
    </Dialog>
  );
}

/** The meeting's link, to send: copied with a click, and its QR code for a phone's camera — which opens no
 * address of this machine's own. The meeting's title, where it has one of its own. */
function InviteDialog() {
  const url = link();
  const title = state.meeting?.title ?? MEETING_TITLE;
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      field.current?.select();
      show("не скопировалось: выделите ссылку и скопируйте её сами", true);
    }
  };
  return (
    <Dialog title="Пригласить">
      <p>
        Отправьте ссылку — по ней войдут {title === MEETING_TITLE ? "в это пространство" : `в пространство «${title}»`}.
      </p>
      <div class="row">
        <input ref={field} readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Ссылка на пространство" />
        <button class="primary" onClick={() => void copy()}>
          {copied ? "Скопировано" : "Скопировать"}
        </button>
      </div>
      <div class="qr invite-qr">
        <QrCode text={url} />
      </div>
      <small class="muted">
        {LOCALHOST.test(location.hostname)
          ? "Телефон не откроет localhost: откройте страницу по адресу этой машины в сети."
          : "Наведите камеру телефона на код — пространство откроется на нём."}
      </small>
      <div class="dialog-buttons">
        <button class="soft" onClick={closeDialog}>
          Готово
        </button>
      </div>
    </Dialog>
  );
}

/** The user's name and picture — a picture uploaded (its middle square) or taken away, a name given —
 * and the account's access code. */
function ProfileDialog() {
  const [name, setName] = useState(state.name);
  const [busy, setBusy] = useState(false);
  const files = useRef<HTMLInputElement>(null);
  const picked = async (file: File | undefined) => {
    if (file === undefined) return;
    setBusy(true);
    try {
      await setAvatar(await pictureOf(file));
    } catch (err) {
      show(err instanceof NotAnImage ? err.message : "не получилось подготовить картинку", true);
    } finally {
      setBusy(false);
    }
  };
  const done = () => {
    rename(name.trim());
    closeDialog();
  };
  return (
    <Dialog title="Профиль">
      <div class="profile">
        <div class={busy ? "profile-picture busy" : "profile-picture"}>
          <Avatar id={state.user} size={96} />
        </div>
        <div class="profile-acts">
          <button class="primary" disabled={busy} onClick={() => files.current?.click()}>
            {busy ? "Загружаю…" : state.avatar === "" ? "Загрузить аватар" : "Сменить аватар"}
          </button>
          {state.avatar !== "" && (
            <button disabled={busy} onClick={dropAvatar}>
              Убрать
            </button>
          )}
          <input
            ref={files}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            hidden
            onChange={(e) => {
              void picked(e.currentTarget.files?.[0]);
              e.currentTarget.value = "";
            }}
          />
          <small class="muted">Квадрат из середины картинки, 256 × 256. Аватар и имя видят все в ваших пространствах.</small>
        </div>
      </div>
      <label for="profile-name">Имя (можно не давать — тогда вы гость)</label>
      <input
        id="profile-name"
        value={name}
        maxLength={40}
        placeholder="Без имени — гость"
        autocomplete="nickname"
        onInput={(e) => setName(e.currentTarget.value)}
        onKeyDown={(e) => e.key === "Enter" && done()}
      />
      {state.meeting !== null && <Access />}
      <LegalLinks />
      <div class="dialog-buttons">
        <button class="soft" onClick={closeDialog}>
          Отмена
        </button>
        <button class="primary" onClick={done}>
          Готово
        </button>
      </div>
    </Dialog>
  );
}
